import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ElementRef,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
} from "@angular/core";
import { ClippingStudioTextEvent } from "app/js/src/domain/clipping-studio/clipping-studio-project";
import { ClippingStudioTextTemplateWritePayload } from "app/js/src/domain/clipping-studio/clipping-studio-text-template";
import { ClippingStudioFontService } from "app/js/src/infrastructure/clipping-studio/clipping-studio-font.service";
import {
  BOLD_FONT_WEIGHT,
  isBoldText,
  REGULAR_FONT_WEIGHT,
} from "app/js/src/domain/clipping-studio/clipping-studio-text-layout";

@Component({
  selector: "evrideo-clipping-studio-text-styling-panel",
  templateUrl: "./clipping-studio-text-styling-panel.component.html",
  styleUrls: ["./clipping-studio-text-styling-panel.component.scss"],
})
export class ClippingStudioTextStylingPanelComponent implements OnChanges {
  @Input() textItem!: ClippingStudioTextEvent;
  @Input() isEditMode = false;
  /** Catalog template id when editor was opened from a template card; otherwise null. */
  @Input() templateId: number | null = null;
  /** Disables template action buttons while an API call is in flight. */
  @Input() templateActionBusy = false;
  /** Current playhead time in ms (for "set to playhead" on In/Out). */
  @Input() currentTimeMs = 0;
  /** FPS for frame-aligned In/Out (so "Now" uses current frame start/end). */
  @Input() fps = 30;
  /** Live preview updates; parent should avoid persisting/undo on each emit. */
  @Output() previewChange = new EventEmitter<Partial<ClippingStudioTextEvent>>();
  @Output() update = new EventEmitter<Partial<ClippingStudioTextEvent>>();
  @Output() close = new EventEmitter<{ saved: boolean }>();
  @Output() saveAsTemplate =
    new EventEmitter<ClippingStudioTextTemplateWritePayload>();
  @Output() updateTemplate = new EventEmitter<{
    id: number;
    payload: ClippingStudioTextTemplateWritePayload;
  }>();
  @Output() deleteTemplate = new EventEmitter<number>();

  @ViewChild("contentInput") contentInputRef: ElementRef<HTMLTextAreaElement> | null = null;

  /** Fallback list used only when the tenant has no fonts registered in the Fonts API. */
  private static readonly DEFAULT_FONTS = ["Inter", "Roboto", "Oswald", "Montserrat", "Playfair Display", "Bebas Neue", "Anton"];
  /** Font family names available for text overlays, sourced from the tenant Fonts API. */
  fonts: string[] = ClippingStudioTextStylingPanelComponent.DEFAULT_FONTS;
  readonly fontSizeOptions = ["12", "14", "16", "18", "20", "24", "32", "40", "48", "64", "72", "96"];
  /** True when the active font file failed to load for preview. */
  fontLoadFailed = false;
  readonly fontLoadErrorTitle = "Font style could not be loaded.";
  readonly fontErrorIcon = "/assets/icons/pending.svg";
  /** Text color presets (custom color via shared picker) */
  readonly colors = [
    "#000000",
    "#ffffff",
    "#00aaff",
    "#e60000",
    "#999999",
    "#00e64d",
    "#ffff00",
  ];
  /** Background color: Transparent, white, black, blue, red, two grays */
  readonly backgroundColors = [
    "#ffffff",
    "#000000",
    "#00aaff",
    "#e60000",
    "#999999",
    "#666666",
  ];

  /** Local draft changes; applied only on Save Changes. */
  private draft: Partial<ClippingStudioTextEvent> = {};

  /**
   * Template id used for Update/Delete. Cleared when textItem switches on preview;
   * re-bound when parent templateId input changes.
   */
  effectiveTemplateId: number | null = null;

  private fontEnsureToken = 0;

  constructor(
    private readonly fontService: ClippingStudioFontService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    const tenantFontNames = this.fontService.getAvailableFontNames();
    if (tenantFontNames.length > 0) {
      this.fonts = tenantFontNames;
    }
  }

  get draftText(): string {
    return this.getDraft("text", this.textItem.text ?? "");
  }

  get showTemplateMutations(): boolean {
    return !!this.effectiveTemplateId;
  }

  ngOnChanges(changes: SimpleChanges): void {
    const textChange = changes["textItem"];
    if (textChange) {
      const prevId = (textChange.previousValue as ClippingStudioTextEvent | undefined)?.id;
      const nextId = (textChange.currentValue as ClippingStudioTextEvent | undefined)?.id;
      if (prevId !== nextId) {
        this.draft = {};
        if (!textChange.firstChange && prevId != null) {
          this.effectiveTemplateId = null;
        }
      }
      void this.refreshActiveFontLoad();
    }

    if (changes["templateId"]) {
      if (this.templateId) {
        this.effectiveTemplateId = this.templateId;
      } else {
        this.effectiveTemplateId = null;
      }
    }
  }

  private getDraft<T>(key: keyof ClippingStudioTextEvent, fallback: T): T {
    const v = this.draft[key] as T | undefined;
    return v !== undefined ? v : fallback;
  }

  get activeFont(): string {
    return this.getDraft("fontFamily", this.textItem.fontFamily || "Inter");
  }

  get activeFontSize(): string {
    return String(this.getDraft("fontSize", this.textItem.fontSize ?? 18));
  }

  get isBold(): boolean {
    return this.getDraft("bold", isBoldText(this.textItem));
  }

  get isItalic(): boolean {
    const fallback = this.textItem.italic ?? this.textItem.fontStyle === "italic";
    return this.getDraft("italic", fallback);
  }

  get activeAlignment(): string {
    return this.getDraft("alignment", this.textItem.alignment || "center");
  }

  get activeColor(): string {
    return this.getDraft("fontColor", this.textItem.fontColor || "#ffffff");
  }

  get isBackgroundTransparent(): boolean {
    const bg = this.getDraft("backgroundColor", this.textItem.backgroundColor);
    return bg == null || bg === "" || bg === "transparent";
  }

  get activeBackgroundColor(): string {
    return this.getDraft("backgroundColor", this.textItem.backgroundColor ?? "#000000");
  }

  get backgroundPadding(): number {
    return this.getDraft("backgroundPadding", this.textItem.backgroundPadding ?? 12);
  }

  get backgroundBorderRadius(): number {
    return this.getDraft("backgroundBorderRadius", this.textItem.backgroundBorderRadius ?? 0);
  }

  get dropShadow(): boolean {
    return this.getDraft("dropShadow", this.textItem.dropShadow ?? false);
  }

  get inTimecode(): string {
    return this.formatMsToTimecode(this.getDraft("inMs", this.textItem.inMs ?? 0));
  }

  get outTimecode(): string {
    return this.formatMsToTimecode(this.getDraft("outMs", this.textItem.outMs ?? 0));
  }

  onTextChange(value: string): void {
    this.draft.text = value;
    this.previewChange.emit({ text: value });
  }

  onFontChange(font: string): void {
    this.draft.fontFamily = font;
    void this.applyFontAfterLoad(font);
  }

  /**
   * Loads the font file first, then emits preview so canvas keeps the previous
   * face until the new one is ready (or falls back on failure).
   */
  private async applyFontAfterLoad(font: string): Promise<void> {
    const token = ++this.fontEnsureToken;
    this.fontLoadFailed = false;
    const result = await this.fontService.ensureFontLoaded(font);
    if (token !== this.fontEnsureToken) {
      return;
    }
    this.fontLoadFailed = !result.loaded;
    this.previewChange.emit({ fontFamily: font });
    this.cdr.markForCheck();
  }

  private async refreshActiveFontLoad(): Promise<void> {
    const token = ++this.fontEnsureToken;
    this.fontLoadFailed = false;
    const result = await this.fontService.ensureFontLoaded(this.activeFont);
    if (token !== this.fontEnsureToken) {
      return;
    }
    this.fontLoadFailed = !result.loaded;
    this.cdr.markForCheck();
  }

  onFontSizeChange(size: string): void {
    const n = parseInt(size, 10);
    this.draft.fontSize = n;
    this.previewChange.emit({ fontSize: n });
  }

  toggleBold(): void {
    const newBold = !this.isBold;
    this.draft.bold = newBold;
    this.draft.fontWeight = newBold ? BOLD_FONT_WEIGHT : REGULAR_FONT_WEIGHT;
    this.previewChange.emit({ bold: newBold, fontWeight: this.draft.fontWeight });
  }

  toggleItalic(): void {
    const newItalic = !this.isItalic;
    this.draft.italic = newItalic;
    this.draft.fontStyle = newItalic ? "italic" : "normal";
    this.previewChange.emit({ italic: newItalic, fontStyle: this.draft.fontStyle });
  }

  setAlignment(alignment: "left" | "center" | "right"): void {
    this.draft.alignment = alignment;
    this.previewChange.emit({ alignment });
  }

  setColor(color: string): void {
    this.draft.fontColor = color;
    this.previewChange.emit({ fontColor: color });
  }

  setBackgroundColor(color: string): void {
    this.draft.backgroundColor = color === "transparent" ? "transparent" : color;
    this.previewChange.emit({ backgroundColor: this.draft.backgroundColor });
  }

  setBackgroundPadding(value: number | string): void {
    const n = typeof value === "string" ? parseInt(value, 10) : value;
    if (!Number.isNaN(n) && n >= 0) {
      this.draft.backgroundPadding = n;
      this.previewChange.emit({ backgroundPadding: n });
    }
  }

  setBackgroundBorderRadius(value: number | string): void {
    const n = typeof value === "string" ? parseInt(value, 10) : value;
    if (!Number.isNaN(n) && n >= 0) {
      this.draft.backgroundBorderRadius = n;
      this.previewChange.emit({ backgroundBorderRadius: n });
    }
  }

  toggleDropShadow(): void {
    this.draft.dropShadow = !this.dropShadow;
    this.previewChange.emit({ dropShadow: this.draft.dropShadow });
  }

  onSave(): void {
    if (Object.keys(this.draft).length > 0) {
      this.update.emit(this.draft);
    }
    this.close.emit({ saved: true });
  }

  onClose(): void {
    this.close.emit({ saved: false });
  }

  onSaveAsTemplate(): void {
    if (this.templateActionBusy) return;
    this.saveAsTemplate.emit(this.buildWritePayload());
  }

  onUpdateTemplate(): void {
    if (this.templateActionBusy || !this.effectiveTemplateId) return;
    this.updateTemplate.emit({
      id: this.effectiveTemplateId,
      payload: this.buildWritePayload(),
    });
  }

  onDeleteTemplate(): void {
    if (this.templateActionBusy || !this.effectiveTemplateId) return;
    this.deleteTemplate.emit(this.effectiveTemplateId);
  }

  focusContent(): void {
    this.contentInputRef?.nativeElement?.focus();
  }

  private buildWritePayload(): ClippingStudioTextTemplateWritePayload {
    const fontWeight = this.isBold ? BOLD_FONT_WEIGHT : REGULAR_FONT_WEIGHT;
    const fontStyle =
      this.getDraft("fontStyle", this.textItem.fontStyle) ??
      (this.isItalic ? "italic" : "normal");
    const payload: ClippingStudioTextTemplateWritePayload = {
      text: this.draftText,
      fontSize: Number(this.activeFontSize),
      fontWeight,
      fontStyle,
      fontColor: this.activeColor,
      fontFamily: this.activeFont,
      alignment: this.activeAlignment,
      bold: this.isBold,
      italic: this.isItalic,
      backgroundColor: this.getDraft(
        "backgroundColor",
        this.textItem.backgroundColor,
      ),
      backgroundPadding: this.backgroundPadding,
      backgroundBorderRadius: this.backgroundBorderRadius,
      dropShadow: this.dropShadow,
      badge: "",
    };

    const shadowColor = this.getDraft("shadowColor", this.textItem.shadowColor);
    if (shadowColor != null && shadowColor !== "") {
      payload.shadowColor = shadowColor;
    }
    const shadowBlur = this.getDraft("shadowBlur", this.textItem.shadowBlur);
    if (typeof shadowBlur === "number") {
      payload.shadowBlur = shadowBlur;
    }
    const shadowOffsetX = this.getDraft(
      "shadowOffsetX",
      this.textItem.shadowOffsetX,
    );
    if (typeof shadowOffsetX === "number") {
      payload.shadowOffsetX = shadowOffsetX;
    }
    const shadowOffsetY = this.getDraft(
      "shadowOffsetY",
      this.textItem.shadowOffsetY,
    );
    if (typeof shadowOffsetY === "number") {
      payload.shadowOffsetY = shadowOffsetY;
    }

    return payload;
  }

  private formatMsToTimecode(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const millis = Math.floor(ms % 1000);
    const pad = (n: number, len = 2) => n.toString().padStart(len, "0");
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}.${pad(millis, 3)}`;
  }

  private parseTimecodeToMs(str: string): number | null {
    const trimmed = str.trim();
    const match = trimmed.match(/^(\d+):(\d{1,2}):(\d{1,2})\.(\d{1,3})$/);
    if (!match) return null;
    const [, h, m, s, ms] = match.map(Number);
    if (m >= 60 || s >= 60 || ms >= 1000) return null;
    return (h * 3600 + m * 60 + s) * 1000 + ms;
  }

  onInTimeBlur(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    const ms = this.parseTimecodeToMs(value);
    if (ms != null) {
      this.draft.inMs = ms;
      this.previewChange.emit({ inMs: ms });
    }
  }

  onOutTimeBlur(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    const ms = this.parseTimecodeToMs(value);
    if (ms != null) {
      this.draft.outMs = ms;
      this.previewChange.emit({ outMs: ms });
    }
  }

  setInToPlayhead(): void {
    const frameMs = this.getFrameDurationMs();
    const inMs = Math.floor(this.currentTimeMs / frameMs) * frameMs;
    this.draft.inMs = inMs;
    this.previewChange.emit({ inMs });
  }

  setOutToPlayhead(): void {
    const frameMs = this.getFrameDurationMs();
    const frameIndex = Math.floor(this.currentTimeMs / frameMs);
    const outMs = (frameIndex + 1) * frameMs;
    this.draft.outMs = outMs;
    this.previewChange.emit({ outMs });
  }

  private getFrameDurationMs(): number {
    const f = this.fps > 0 ? this.fps : 30;
    return 1000 / f;
  }
}
